<template>
	<div>
		<component
			v-if="pickerComponent"
			:is="pickerComponent"
			:context="context"
			:mode="mode"
			:readonly="readonly"
			:selectedGoals="selectedGoals"
			:suggestedGoals="suggestedGoals"
			@requestUnmount="$emit('requestUnmount')"
			@opened="$emit('opened')"
			@goalSelected="addGoal"
			@goalUnselected="removeGoal"
		/>
	</div>
</template>

<script lang="ts">
import { Goal } from "@/models/Goal.model.ts";
import { Curriculum } from "@/models/Curriculum.model.ts";
import { resolveExternalPickerComponent } from "@/goals/utils/GoalPickerLaunch.util";

/**
 * The external (vendor) goal picker registered for a given curriculum.
 *
 * Resolves which picker component (GoalPickerZill/Ovsg/OpStap) serves the given
 * curriculum via the registry's `externalPicker` field and renders it, relaying the
 * shared prop/event contract. Renders nothing for a curriculum without an external
 * picker — callers are expected to check `curriculum.hasExternalGoalPicker()` /
 * `hasInlineGoalViewer()` and fall back to the generic GoalSelector drawer themselves.
 *
 * `mode` selects the vendor widget's variant:
 * - `picker` — the full selection UI (used by GoalSelector for fixed-curriculum flows);
 * - `inline` — the embedded read-only/inline viewer (GoalsField, GoalsDisplayGroup;
 *   only curricula with `inlineViewer` in the registry).
 *
 * Note: the goal selector bar's launch flow does NOT pass through this component — its
 * launch descriptor already carries the resolved picker component (GoalPickerLaunch.util).
 * Use this where you hold a `Curriculum` and want the mapping done for you.
 */
export default {
	emits: ['requestUnmount', 'goalSelected', 'goalUnselected', 'opened'],

	props: {
		curriculum: {
			type: Curriculum,
			required: true,
		},

		selectedGoals: {
			type: Array,
			default: () => [],
		},

		suggestedGoals: {
			type: Array,
			default: () => [],
		},

		context: {
			type: String,
		},

		mode: {
			type: String,
			default: 'picker',
			validator: (value: string) => ['picker', 'inline'].includes(value),
		},

		readonly: {
			type: Boolean,
			default: false,
		},
	},

	computed: {
		pickerComponent() {
			return resolveExternalPickerComponent(this.curriculum);
		},
	},

	methods: {
		addGoal(goal: Goal) {
			this.$emit('goalSelected', goal);
		},

		removeGoal(goal: Goal) {
			this.$emit('goalUnselected', goal);
		},
	},
}
</script>
